// Turns the engine suggestion + last session + any accepted coach override into
// pre-filled sets for the next session, so capture is mostly tapping "confirm".
// The coach can only nudge load/reps; it can never override the engine's structural
// decision in a way that contradicts it (load overrides are additive judgement).

import type { Exercise, SetLog, SuggestedAdjustment } from './types';
import { suggestProgression, backoffWeightKg, type ExerciseHistoryPoint, type Suggestion } from './progression';
import { workingSets } from './estimate';
import { round2 } from './weight';

export interface PlannedSet {
  weightKg: number;
  targetReps: number;
  isWarmup: boolean;
  // For ramp_top_backoff, label the role so the UI can show "top" / "back-off".
  role: 'working' | 'top' | 'backoff';
}

export interface ExercisePlan {
  exerciseId: string;
  suggestion: Suggestion;
  beatThis: SetLog[];
  planned: PlannedSet[];
  coachOverride: SuggestedAdjustment | null;
}

function weightWentUp(s: Suggestion): boolean {
  return s.kind === 'increase_weight' || s.kind === 'amrap_add_load' || s.kind === 'topset_increase';
}

export function buildExercisePlan(
  ex: Exercise,
  history: ExerciseHistoryPoint[],
  targetSets: number,
  accepted: SuggestedAdjustment | null = null,
): ExercisePlan {
  const suggestion = suggestProgression(ex, history);
  const last = history[0];
  const beatThis = last ? workingSets(last.sets) : [];

  // The weight Marko actually used last time (ramp uses the heaviest = top set).
  const lastWeight = beatThis.length
    ? (ex.structure === 'ramp_top_backoff'
        ? Math.max(...beatThis.map((s) => s.weightKg))
        : beatThis[0].weightKg)
    : null;

  // Working weight to pre-fill (total kg). Precedence:
  //  1. an explicit coach weight (load/hold/deload with a number),
  //  2. kind 'hold' -> pin to last weight, ignoring the engine's earned bump,
  //  3. kind 'deload' -> last weight minus 10%,
  //  4. otherwise the engine's suggestion.
  let weight: number | null;
  if (accepted?.suggestedWeightKg != null) {
    weight = accepted.suggestedWeightKg;
  } else if (accepted?.kind === 'hold' && lastWeight != null) {
    weight = lastWeight;
  } else if (accepted?.kind === 'deload' && lastWeight != null) {
    weight = round2(lastWeight * 0.9);
  } else {
    weight = suggestion.targetWeightKg;
  }
  if (weight == null) weight = beatThis[0]?.weightKg ?? 0;

  const range = accepted?.suggestedRepRange ?? suggestion.targetRepRange;
  const nSets = Math.max(targetSets, beatThis.length || targetSets);

  const planned: PlannedSet[] = [];

  if (ex.structure === 'ramp_top_backoff') {
    const topReps = range.top;
    // When the coach pinned/cut the top set, derive the back-off from that weight
    // rather than the engine's (which assumed a different top).
    const own = backoffWeightKg(weight, ex, last?.bodyweightKg ?? null);
    const backoff = accepted ? own : (suggestion.backoffWeightKg ?? own);
    planned.push({ weightKg: weight, targetReps: topReps, isWarmup: false, role: 'top' });
    for (let i = 1; i < nSets; i++) {
      planned.push({ weightKg: backoff, targetReps: 12, isWarmup: false, role: 'backoff' });
    }
    return { exerciseId: ex.id, suggestion, beatThis, planned, coachOverride: accepted };
  }

  for (let i = 0; i < nSets; i++) {
    // Pre-fill reps: when the weight just went up, aim at the bottom of the range;
    // otherwise target last session's reps (the number to beat), defaulting to top.
    let targetReps: number;
    if (weightWentUp(suggestion) && accepted == null) {
      targetReps = range.bottom;
    } else {
      targetReps = beatThis[i]?.reps ?? range.top;
    }
    planned.push({ weightKg: weight, targetReps, isWarmup: false, role: 'working' });
  }

  return { exerciseId: ex.id, suggestion, beatThis, planned, coachOverride: accepted };
}
